import { memo, useCallback, useMemo } from 'react';
import {
  useThemeStore,
  selectTheme,
  selectSetTheme,
  type Theme,
} from '../../store/useThemeStore';

/**
 * Sun icon for light mode
 */
const SunIcon = memo(function SunIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
});

/**
 * Moon icon for dark mode
 */
const MoonIcon = memo(function MoonIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
      />
    </svg>
  );
});

/**
 * System/Auto icon
 */
const SystemIcon = memo(function SystemIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
});

/**
 * Theme toggle button component
 * Cycles through light -> dark -> system themes
 */
const ThemeToggle = memo(function ThemeToggle() {
  const theme = useThemeStore(selectTheme);
  const setTheme = useThemeStore(selectSetTheme);

  // Cycle through themes
  const handleToggle = useCallback(() => {
    const nextTheme: Record<Theme, Theme> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    };
    setTheme(nextTheme[theme]);
  }, [theme, setTheme]);

  // Get current icon based on theme
  const currentIcon = useMemo(() => {
    switch (theme) {
      case 'light':
        return <SunIcon />;
      case 'dark':
        return <MoonIcon />;
      case 'system':
        return <SystemIcon />;
    }
  }, [theme]);

  // Get aria label
  const ariaLabel = useMemo(() => {
    const labels: Record<Theme, string> = {
      light: 'Light theme active, switch to dark theme',
      dark: 'Dark theme active, switch to system theme',
      system: 'System theme active, switch to light theme',
    };
    return labels[theme];
  }, [theme]);

  return (
    <button
      onClick={handleToggle}
      className="p-2 rounded-lg text-gray-600 dark:text-gray-300 
                 hover:bg-gray-100 dark:hover:bg-gray-700
                 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                 dark:focus:ring-offset-gray-800
                 transition-colors"
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      {currentIcon}
    </button>
  );
});

export default ThemeToggle;
